import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../context/AuthContext';
import { ConfigContext } from '../../../../context/ConfigContext';
import * as actionType from '../../../../store/actions';
import useWindowSize from '../../../../hooks/useWindowSize';

// ── Logo — NOVA.png in your assets ──────────────────────────────
import logo from '../../../../assets/images/NOVA.png';
import ThemeToggle from '../../../../components/ThemeToggle';

// ─────────────────────────────────────────────────────────────────
// STYLES
// ─────────────────────────────────────────────────────────────────
const SIDEBAR_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap');

  /* Reset Datta Able navbar-content so our sidebar owns the space */
  .navbar-content, .datta-scroll, .pcoded-inner-navbar { all: unset !important; }
  .navbar-wrapper { display: flex !important; flex-direction: column !important; height: 100% !important; padding: 0 !important; overflow: hidden !important; }

  /* ── Shell ── */
  .ns-shell {
    display: flex; flex-direction: column;
    height: 100vh; width: 60px;
    background: var(--nd-surface);
    font-family: 'DM Sans', sans-serif;
    overflow: hidden; position: relative;
    transition: width 0.25s cubic-bezier(0.4,0,0.2,1);
  }
  .ns-shell:hover { width: 220px; }
  .ns-shell.ns-collapsed { width: 60px; }
  .ns-shell.ns-collapsed:hover { width: 220px; }

  /* Subtle dot-grid texture */
  .ns-shell::before {
    content: '';
    position: absolute; inset: 0;
    background-image: radial-gradient(circle, var(--nd-w03) 1px, transparent 1px);
    background-size: 20px 20px;
    pointer-events: none; z-index: 0;
  }
  .ns-shell > * { position: relative; z-index: 1; }
  .ns-shell .ns-logo-full { display: none; }
  .ns-shell:hover .ns-logo-full { display: block; }
  /* Theme toggle: lives in the header, revealed (like the full logo) on hover */
  .ns-shell .ns-toggle-wrap { display: none; margin-left: auto; flex-shrink: 0; line-height: 0; }
  .ns-shell:hover .ns-toggle-wrap { display: block; }

  /* ── Scrollbar ── */
  .ns-scroll::-webkit-scrollbar { width: 3px; }
  .ns-scroll::-webkit-scrollbar-thumb { background: var(--nd-w08); border-radius: 4px; }

  /* ── Nav button ── */
  .ns-btn {
    width: 100%; display: flex; align-items: center; gap: 9px;
    padding: 7px 10px; border-radius: 9px; border: none;
    background: transparent; cursor: pointer;
    transition: background 0.15s, color 0.15s;
    position: relative; text-align: left;
    text-decoration: none !important; color: inherit;
    white-space: nowrap; overflow: hidden;
  }
  .ns-btn:hover,
  .ns-btn:focus { background: var(--nd-w04); outline: none; text-decoration: none !important; }

  /* Active state */
  .ns-btn.active,
  .ns-btn.ns-active {
    background: linear-gradient(135deg, rgba(59,130,246,0.13), rgba(34,211,165,0.07)) !important;
  }
  .ns-btn.active::before,
  .ns-btn.ns-active::before {
    content: ''; position: absolute; left: -10px; top: 50%;
    transform: translateY(-50%); width: 3px; height: 55%;
    border-radius: 0 3px 3px 0;
    background: linear-gradient(180deg, #3B82F6, #22D3A5);
    box-shadow: 0 0 8px rgba(59,130,246,0.55);
  }
  .ns-btn.active .ns-icon-box,
  .ns-btn.ns-active .ns-icon-box {
    background: linear-gradient(135deg, rgba(59,130,246,0.22), rgba(34,211,165,0.12)) !important;
    border-color: rgba(59,130,246,0.38) !important;
  }
  .ns-btn.active .ns-icon-box i,
  .ns-btn.ns-active .ns-icon-box i { color: #60A5FA !important; }
  .ns-btn.active .ns-lbl,
  .ns-btn.ns-active .ns-lbl { color: var(--nd-text-strong) !important; font-weight: 700 !important; }

  /* ── Icon box ── */
  .ns-icon-box {
    width: 27px; height: 27px; border-radius: 7px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    background: var(--nd-w03);
    border: 1px solid var(--nd-w06);
    transition: all 0.15s;
  }
  .ns-icon-box i { font-size: 12px; color: var(--nd-tx30); transition: color 0.15s; }
  .ns-btn:hover .ns-icon-box { background: var(--nd-w06); border-color: var(--nd-w10); }
  .ns-btn:hover .ns-icon-box i { color: var(--nd-tx60); }

  /* ── Label ── */
  .ns-lbl {
    font-size: 11px; flex: 1; line-height: 1.3;
    color: var(--nd-tx40); transition: color 0.15s, opacity 0.2s;
    font-weight: 500; overflow: hidden; white-space: nowrap;
  }
  .ns-btn:hover .ns-lbl { color: var(--nd-tx85); }

  /* ── Badge ── */
  .ns-badge {
    font-family: 'DM Mono', monospace; font-size: 8px; font-weight: 500;
    padding: 2px 6px; border-radius: 20px; flex-shrink: 0;
    background: rgba(96,165,250,0.12); color: #60A5FA;
    border: 1px solid rgba(96,165,250,0.22); line-height: 1.5;
    transition: opacity 0.2s;
  }

  /* ── Group label ── */
  .ns-group-label {
    font-size: 7px; font-weight: 800; letter-spacing: 0.22em;
    color: var(--nd-w20); padding: 0 8px; margin-bottom: 5px;
    text-transform: uppercase;
    transition: opacity 0.2s, height 0.2s;
    overflow: hidden; white-space: nowrap;
  }
  .ns-group-rule {
    height: 1px; margin-bottom: 7px; margin-left: 8px;
    background: linear-gradient(90deg, rgba(59,130,246,0.28), transparent);
    transition: opacity 0.2s;
  }

  /* ── Collapsed: icon-only mode ── */
  .ns-shell:not(:hover) .ns-lbl,
  .ns-shell:not(:hover) .ns-badge,
  .ns-shell:not(:hover) .ns-caret { opacity: 0; width: 0; overflow: hidden; flex: none; }
  .ns-shell:not(:hover) .ns-group-label { opacity: 0; height: 0; margin: 0; padding: 0; }
  .ns-shell:not(:hover) .ns-group-rule { opacity: 0; height: 0; margin: 0; }
  .ns-shell:not(:hover) .ns-btn {
    justify-content: center;
    padding: 7px 0;
    gap: 0;
    width: 40px;
    margin: 0 auto;
  }
  .ns-shell:not(:hover) .ns-scroll { padding: 12px 6px; }
  .ns-shell:not(:hover) .ns-group-wrap { display: flex; flex-direction: column; align-items: center; }
  .ns-shell:not(:hover) .ns-item-wrap { width: 100%; display: flex; justify-content: center; }
  /* On hover — restore everything */
  .ns-shell:hover .ns-lbl { opacity: 1; width: auto; flex: 1; }
  .ns-shell:hover .ns-badge { opacity: 1; width: auto; }
  .ns-shell:hover .ns-caret { opacity: 1; width: auto; }
  .ns-shell:hover .ns-group-label { opacity: 1; height: auto; padding: 0 8px; margin-bottom: 5px; }
  .ns-shell:hover .ns-group-rule { opacity: 1; height: 1px; margin-bottom: 7px; margin-left: 8px; }
  .ns-shell:hover .ns-btn { justify-content: flex-start; padding: 7px 10px; gap: 9px; width: 100%; margin: 0; }
  .ns-shell:hover .ns-scroll { padding: 12px 10px; }
  .ns-shell:hover .ns-group-wrap { align-items: stretch; }
  .ns-shell:hover .ns-item-wrap { justify-content: stretch; }

  /* Collapsed tooltip on hover */
  .ns-shell.ns-collapsed .ns-btn { position: relative; }
  .ns-shell.ns-collapsed .ns-btn[data-tip]:hover::after {
    content: attr(data-tip);
    position: absolute; left: calc(100% + 10px); top: 50%;
    transform: translateY(-50%);
    background: var(--nd-surface-3); color: var(--nd-text);
    font-size: 11px; font-weight: 600; font-family: 'DM Sans', sans-serif;
    padding: 5px 10px; border-radius: 8px; white-space: nowrap;
    border: 1px solid var(--nd-w10);
    box-shadow: 0 4px 16px rgba(0,0,0,0.4);
    z-index: 9999; pointer-events: none;
  }
  .ns-shell.ns-collapsed .ns-btn[data-tip]:hover::before {
    content: '';
    position: absolute; left: calc(100% + 4px); top: 50%;
    transform: translateY(-50%);
    border: 5px solid transparent;
    border-right-color: var(--nd-surface-3);
    z-index: 9999; pointer-events: none;
  }

  /* Collapsed: bottom section */
  .ns-shell:not(:hover) .ns-noti-label,
  .ns-shell:not(:hover) .ns-noti-count,
  .ns-shell:not(:hover) .ns-user-info,
  .ns-shell:not(:hover) .ns-user-expand { display: none !important; }
  .ns-shell:not(:hover) .ns-noti-btn { justify-content: center; padding: 7px 0; }
  .ns-shell:not(:hover) .ns-user-card .ns-user-row { justify-content: center; padding: 10px 0; }
  .ns-shell:hover .ns-noti-btn { justify-content: flex-start; padding: 7px 10px; }
  .ns-shell:hover .ns-noti-label,
  .ns-shell:hover .ns-noti-count { display: inline !important; }
  .ns-shell:hover .ns-user-info { display: block !important; }
  .ns-shell:hover .ns-user-card .ns-user-row { justify-content: flex-start; padding: 10px 12px; }



  /* ── Notification button ── */
  .ns-noti-btn {
    width: 100%; display: flex; align-items: center; gap: 9px;
    padding: 7px 10px; border-radius: 9px; border: none;
    background: var(--nd-w03); cursor: pointer;
    transition: all 0.15s; margin-bottom: 6px;
    border: 1px solid var(--nd-w05);
    text-align: left; overflow: hidden; white-space: nowrap;
  }
  .ns-noti-btn:hover { background: var(--nd-w08); border-color: var(--nd-w08); }

  /* ── User card ── */
  .ns-user-card {
    border-radius: 10px; overflow: hidden;
    border: 1px solid var(--nd-w08);
    background: var(--nd-w03); cursor: pointer;
    transition: border-color 0.15s;
  }
  .ns-user-card:hover { border-color: var(--nd-w12); }
  .ns-user-row {
    display: flex; align-items: center; gap: 9px; padding: 10px 12px;
    transition: padding 0.25s;
  }

  /* ── User menu items ── */
  .ns-umenu-btn {
    display: flex; align-items: center; gap: 10px;
    padding: 8px 10px; border-radius: 8px; cursor: pointer;
    transition: background 0.15s; border: none; background: transparent;
    width: 100%; text-align: left;
  }
  .ns-umenu-btn:hover { background: var(--nd-w06); }

  /* ── Online dot ── */
  .ns-online-dot {
    width: 6px; height: 6px; border-radius: 50%;
    background: #22D3A5; box-shadow: 0 0 5px #22D3A5;
    position: absolute; bottom: 0; right: 0;
    border: 1.5px solid var(--nd-surface);
  }

  /* ── Stagger animations ── */
  .ns-group-wrap { animation: nsGrpIn 0.3s cubic-bezier(0.34,1.2,0.64,1) both; }
  .ns-group-wrap:nth-child(1){animation-delay:.04s}
  .ns-group-wrap:nth-child(2){animation-delay:.09s}
  .ns-group-wrap:nth-child(3){animation-delay:.14s}
  .ns-group-wrap:nth-child(4){animation-delay:.18s}
  @keyframes nsGrpIn { from{opacity:0;transform:translateX(-8px)} to{opacity:1;transform:translateX(0)} }

  .ns-item-wrap { animation: nsItmIn 0.28s cubic-bezier(0.34,1.2,0.64,1) both; }
  .ns-item-wrap:nth-child(1){animation-delay:.06s} .ns-item-wrap:nth-child(2){animation-delay:.09s}
  .ns-item-wrap:nth-child(3){animation-delay:.12s} .ns-item-wrap:nth-child(4){animation-delay:.15s}
  .ns-item-wrap:nth-child(5){animation-delay:.18s} .ns-item-wrap:nth-child(6){animation-delay:.21s}
  .ns-item-wrap:nth-child(7){animation-delay:.24s} .ns-item-wrap:nth-child(8){animation-delay:.27s}
  @keyframes nsItmIn { from{opacity:0;transform:translateY(5px)} to{opacity:1;transform:translateY(0)} }

  .ns-user-expand { animation: nsExpand 0.2s ease both; }
  @keyframes nsExpand { from{opacity:0;transform:translateY(-4px)} to{opacity:1;transform:translateY(0)} }

  /* ── Caret rotation ── */
  .ns-caret { display:inline-block; transition: transform 0.2s ease; font-size: 9px; color: var(--nd-w20); flex-shrink: 0; }
  .ns-caret.open { transform: rotate(180deg); }
`;

// ─────────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────────
const NavContent = ({ navigation }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const windowSize = useWindowSize();
  const configContext = useContext(ConfigContext);
  const { collapseMenu } = configContext.state;
  const { dispatch } = configContext;

  const { user } = useAuth();
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const firstName = user?.firstName || 'User';
  const lastName  = user?.lastName  || '';
  const userRole  = user?.role      || '';

  const handleNavClick = () => {
    if (windowSize.width < 992) {
      dispatch({ type: actionType.COLLAPSE_MENU });
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  return (
    <>
      <style>{SIDEBAR_CSS}</style>

      <div className="ns-shell ns-collapsed">

        {/* ────────────────────────────────────────
            LOGO + TOGGLE
        ──────────────────────────────────────── */}
        <div style={{
          padding: '14px 14px 12px',
          borderBottom: '1px solid var(--nd-w06)',
          flexShrink: 0,
          display: 'flex', alignItems: 'center',
          overflow: 'hidden',
          minHeight: 57,
        }}>
          {/* N mark always visible; full logo shown on hover via CSS */}
          <div className="ns-logo-icon" style={{
            width: 27, height: 27, borderRadius: 7, flexShrink: 0,
            background: 'linear-gradient(135deg,#3B82F6,#22D3A5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, fontWeight: 900, color: 'var(--nd-bg)',
            boxShadow: '0 3px 12px rgba(59,130,246,0.3)',
          }}>N</div>
          <img
            src={logo}
            alt="NOVAA"
            className="ns-logo-full"
            style={{ height: 30, width: 'auto', objectFit: 'contain', marginLeft: 8, minWidth: 0 }}
            onError={e => { e.target.style.display = 'none'; }}
          />
          <div className="ns-toggle-wrap"><ThemeToggle /></div>
        </div>



        {/* ────────────────────────────────────────
            NAV GROUPS (scrollable)
        ──────────────────────────────────────── */}
        <div
          className="ns-scroll"
          style={{
            flex: 1, overflowY: 'auto', overflowX: 'hidden',
            padding: '12px 10px',
            display: 'flex', flexDirection: 'column', gap: 16,
          }}
        >
          {navigation.map((group, gi) => {
            if (group.type !== 'group' || !group.children?.length) return null;

            return (
              <div key={group.id || gi} className="ns-group-wrap">
                <div className="ns-group-label">{group.title}</div>
                <div className="ns-group-rule" />

                <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {group.children.map((item, ii) => {
                    if (item.type !== 'item') return null;
                    const isActive = location.pathname === item.url;

                    return (
                      <div key={item.id || ii} className="ns-item-wrap">
                        {item.external ? (
                          <a
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ns-btn"
                            data-tip={item.title}
                          >
                            <div className="ns-icon-box">
                              {item.icon && <i className={item.icon} />}
                            </div>
                            <span className="ns-lbl">{item.title}</span>
                            {item.badge && <span className="ns-badge">{item.badge.title || item.badge}</span>}
                          </a>
                        ) : (
                          <NavLink
                            to={item.url}
                            className={({ isActive: navActive }) => `ns-btn${navActive ? ' active' : ''}`}
                            onClick={handleNavClick}
                            data-tip={item.title}
                          >
                            <div className="ns-icon-box">
                              {item.icon && <i className={item.icon} />}
                            </div>
                            <span className="ns-lbl">{item.title}</span>
                            {item.badge && <span className="ns-badge">{item.badge.title || item.badge}</span>}
                          </NavLink>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* ────────────────────────────────────────
            BOTTOM: Notifications + User card
        ──────────────────────────────────────── */}
        <div style={{
          padding: '10px',
          borderTop: '1px solid var(--nd-w05)',
          flexShrink: 0,
        }}>

          {/* Notifications — hidden in the customer portal (not in use yet) */}
          {userRole !== 'customer' && (
          <button
            className="ns-noti-btn"
            onClick={() => navigate('/notifications')}

          >
            <div style={{
              width: 27, height: 27, borderRadius: 7, flexShrink: 0,
              background: 'var(--nd-w04)',
              border: '1px solid var(--nd-w08)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              position: 'relative',
            }}>
              <i className="feather icon-bell" style={{ fontSize: 12, color: 'var(--nd-tx35)' }} />
              <div style={{
                position: 'absolute', top: 4, right: 4,
                width: 6, height: 6, borderRadius: '50%',
                background: '#EF4444', border: '1.5px solid var(--nd-surface)',
                boxShadow: '0 0 5px #EF4444',
              }} />
            </div>
            <span className="ns-noti-label" style={{ fontSize: 11, fontWeight: 500, color: 'var(--nd-tx40)', flex: 1 }}>
              Notifications
            </span>
            <span className="ns-noti-count" style={{
              fontSize: 8, fontFamily: "'DM Mono', monospace",
              padding: '2px 6px', borderRadius: 20,
              background: 'rgba(239,68,68,0.12)', color: '#F87171',
              border: '1px solid rgba(239,68,68,0.2)', lineHeight: 1.5,
            }}>1</span>
          </button>
          )}

          {/* User card */}
          <div
            className="ns-user-card"
            onClick={() => setUserMenuOpen(o => !o)}
            role="button"
            tabIndex={0}
            onKeyDown={e => e.key === 'Enter' && setUserMenuOpen(o => !o)}
          >
            <div className="ns-user-row">
                <div style={{ position: 'relative', flexShrink: 0 }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: 8,
                    background: 'linear-gradient(135deg,#3B82F6,#A78BFA)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 12, fontWeight: 800, color: 'var(--nd-text-strong)',
                  }}>
                    {firstName[0]?.toUpperCase() || 'U'}
                  </div>
                  <div className="ns-online-dot" />
                </div>
                <div className="ns-user-info" style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 11, fontWeight: 700, color: 'var(--nd-text)',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {firstName} {lastName}
                  </div>
                  <div style={{ fontSize: 8, color: 'var(--nd-tx30)', letterSpacing: '0.05em', marginTop: 1 }}>
                    {userRole}
                  </div>
                </div>
                <span className={`ns-caret ${userMenuOpen ? 'open' : ''}`}>▾</span>
              </div>

              {userMenuOpen && (
                <div
                  className="ns-user-expand"
                  style={{ borderTop: '1px solid var(--nd-w06)', padding: '4px 6px 6px' }}
                  onClick={e => e.stopPropagation()}
                >
                  {[
                    { icon: 'feather icon-settings', label: 'Settings', color: '#A78BFA', path: '/settings'  },
                    { icon: 'feather icon-user',     label: 'Profile',  color: '#60A5FA', path: '/profile'   },
                    { icon: 'feather icon-mail',     label: 'Messages', color: '#F59E0B', path: '/messages'  },
                  ].map(({ icon, label, color, path }) => (
                    <button
                      key={label}
                      className="ns-umenu-btn"
                      onClick={() => { navigate(path); setUserMenuOpen(false); handleNavClick(); }}
                    >
                      <div style={{
                        width: 24, height: 24, borderRadius: 6, flexShrink: 0,
                        background: color + '18', border: `1px solid ${color}25`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <i className={icon} style={{ fontSize: 12, color }} />
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--nd-tx55)' }}>{label}</span>
                    </button>
                  ))}

                  <div style={{ height: 1, background: 'var(--nd-w05)', margin: '4px 6px' }} />

                  <button className="ns-umenu-btn" onClick={handleLogout}>
                    <div style={{
                      width: 24, height: 24, borderRadius: 6, flexShrink: 0,
                      background: 'rgba(248,113,113,0.12)', border: '1px solid rgba(248,113,113,0.2)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <i className="feather icon-log-out" style={{ fontSize: 12, color: '#F87171' }} />
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#F87171' }}>Log Out</span>
                  </button>
                </div>
              )}
            </div>


        </div>
      </div>
    </>
  );
};

NavContent.propTypes = {
  navigation: PropTypes.array
};

export default NavContent;